import type { PrettifyType } from "../types/PrettifyType";

/**
 * Apply transformations to a given object. Omitted properties are not
 * transformed.
 *
 * @param obj     - The object you wish to transform.
 * @param mapping - A mapping where the key is a property in object and value
 *                  is the transformation you wish to transform.
 *
 * @example
 *
 * transform({ id: 1, name: "Bob" }, { id: (num) => num.toString() })
 * //=> { id: "1", name: "Bob" }
 **/
export default function transform<
  From extends Mappable,
  Mapping extends KeyTransformMap<From> = KeyTransformMap<From>,
>(obj: From, mapping: Mapping): TransformedType<From, Mapping> {
  const entries = Object.entries(mapping);
  const newEntries = entries.map(([key, mapVal]) => [key, mapVal(obj[key])]);
  return { ...obj, ...Object.fromEntries(newEntries) };
}

type Mappable = Record<string, unknown>;

export type KeyTransformMap<T extends Mappable> = {
  [key in keyof T]?: TransformFunction<T, key>;
};

type TransformedType<
  T extends Mappable,
  TransformMap extends KeyTransformMap<T>,
> = PrettifyType<{
  [key in keyof T]: TransformMap[key] extends TransformFunction<T, key>
    ? ReturnType<TransformMap[key]>
    : T[key];
}>;

type TransformFunction<
  T extends Mappable,
  K extends keyof T,
  Output = unknown,
> = (value: T[K]) => Output;
